"use client";

import * as React from "react";
import { FileUpload } from "./ui/file-upload";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Loader2, Upload, Check, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { WidgetContainer } from "./ui/widget-container";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { avoid, recommended } from "@/constants/pic-guide";

// Update the GarmentType definition to match the API requirements
type GarmentType = "tops" | "bottoms" | "one-pieces";

interface VirtualFittingRoomProps {
  garmentId: string;
  garmentImage: string;
  garmentName: string;
  garmentType: GarmentType;
  apiKey: string;
  token: string;
  widgetClasses: string;
  onClose: () => void;
}

export function VirtualFittingRoom({
  garmentId,
  garmentImage,
  garmentName,
  garmentType: initialGarmentType,
  apiKey,
  token,
  widgetClasses,
  onClose,
}: VirtualFittingRoomProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [userPhoto, setUserPhoto] = React.useState<string>();
  const [previewImage, setPreviewImage] = React.useState<string>();
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [selectedGarmentType, setSelectedGarmentType] =
    React.useState<GarmentType>(initialGarmentType);
  const { toast } = useToast();

  const handleFileUpload = async (file: File) => {
    try {
      setIsLoading(true);
      setError(undefined);

      setUserPhoto(URL.createObjectURL(file)); // Create a local preview URL
      setSelectedFile(file); // Store the file for generating the preview

      toast({
        title: "Success",
        description:
          "Photo uploaded successfully. Select the garment type and click 'Try It On' to see how it looks!",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to upload photo";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeneratePreview = async () => {
    if (!userPhoto || !selectedFile) {
      // Add null check
      toast({
        title: "Error",
        description: "Please upload a photo first",
        variant: "destructive",
      });
      return;
    }
    const formData = new FormData();
    formData.append("dressImage", garmentImage);
    formData.append("modelImage", selectedFile);
    formData.append("category", selectedGarmentType);

    try {
      setIsLoading(true);
      setError(undefined);
      setPreviewImage(undefined);

      const res = await fetch("https://www.aiframe.app/api/virtual-tryon", {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: {
          "api-key": apiKey,
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        const errorMessage =
          data.error || data.message || "Failed to generate preview";
        throw new Error(errorMessage);
      }

      if (res.ok && !data.success) {
        const errorMessage =
          data.error || data.message || "Failed to generate preview";
        throw new Error(errorMessage);
      }

      if (!data.generatedImageUrl) {
        throw new Error("Invalid response from server");
      }

      setPreviewImage(data.generatedImageUrl);
      toast({
        title: "Success",
        description: "Virtual try-on completed!",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to generate preview";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    const handleOpen = (
      e: CustomEvent<{
        garmentId: number;
        garmentImage: string;
        garmentName: string;
        garmentType: GarmentType;
      }>
    ) => {
      setIsOpen(true);
      setError(undefined);
      setSelectedGarmentType(e.detail.garmentType);
    };

    window.addEventListener(
      "openVirtualFittingRoom",
      handleOpen as EventListener
    );
    return () => {
      window.removeEventListener(
        "openVirtualFittingRoom",
        handleOpen as EventListener
      );
    };
  }, []);

  React.useEffect(() => {
    if (garmentId) {
      setIsOpen(true);
      setError(undefined);
    }
  }, [garmentId]);

  const handleClose = () => {
    setIsOpen(false);
    setUserPhoto(undefined);
    setPreviewImage(undefined);
    setError(undefined);
    onClose();
  };

  const handleRetry = () => {
    setError(undefined);
    setPreviewImage(undefined);
    if (!userPhoto) {
      setUserPhoto(undefined);
    }
  };

  return (
    <WidgetContainer
      isOpen={isOpen}
      onClose={handleClose}
      className={cn("lg:w-[70%]", widgetClasses)}
    >
      <div className="flex flex-col h-full max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Virtual Fitting Room</h1>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                !userPhoto
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              1
            </div>
            <span className="font-medium hidden sm:inline">Upload Photo</span>
          </div>
          <div className="h-px flex-1 bg-border bg-black" />
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                userPhoto && !previewImage
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              2
            </div>
            <span className="font-medium hidden sm:inline">Try It On</span>
          </div>
          <div className="h-px flex-1 bg-border" />
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                previewImage
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              3
            </div>
            <span className="font-medium hidden sm:inline">View Result</span>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            variant="destructive"
            className="mb-6 bg-red-700/20 text-red-700 !rounded-md"
          >
            <div className="flex flex-col md:flex-row md:justify-between ">
              <div className=" flex gap-2">
                <AlertCircle className="h-4 w-4" />

                <div>
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          </Alert>
        )}

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-6 flex-1">
          {/* Left Panel: User Photo */}
          <Card className="h-full">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-4">Your Photo</h2>
              {!userPhoto ? (
                <>
                  <p className="text-muted-foreground mb-6">
                    Upload a photo to try on {garmentName}
                  </p>
                  <FileUpload
                    onFileSelected={handleFileUpload}
                    accept="image/jpeg,image/png"
                    maxSize={10 * 1024 * 1024}
                  />
                  <div className="flex flex-col gap-3 mt-2">
                    <div>
                      <h5 className="font-bold">Recommended:</h5>
                      <ul className="grid grid-cols-2 gap-1 mt-2 text-xs list-outside pl-4 ">
                        {recommended.map((item) => (
                          <li className="list-disc" key={item.id}>
                            {item.title}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-bold">Pics to Avoid:</h5>
                      <ul className="text-xs list-outside pl-4 flex flex-col gap-1 mt-2 ">
                        {avoid.map((item) => (
                          <li className="list-disc" key={item.id}>
                            {item.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </>
              ) : (
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={previewImage || userPhoto}
                    alt="Preview"
                    className="object-cover w-full h-full"
                  />
                  {!previewImage && !isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                      <div className="w-full max-w-xs space-y-4 p-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Select Garment Type
                          </label>
                          <Select
                            value={selectedGarmentType}
                            onValueChange={(value) =>
                              setSelectedGarmentType(value as GarmentType)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="tops">
                                Tops (Shirts, Jackets)
                              </SelectItem>
                              <SelectItem value="bottoms">
                                Bottoms (Pants, Skirts)
                              </SelectItem>
                              <SelectItem value="one-pieces">
                                One Pieces (Dresses, Suits)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          size="lg"
                          onClick={handleGeneratePreview}
                          disabled={isLoading}
                          className="w-full "
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Try It On
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setUserPhoto(undefined);
                            setPreviewImage(undefined);
                            setError(undefined);
                          }}
                          className="w-full"
                        >
                          Upload Different Photo
                        </Button>
                      </div>
                    </div>
                  )}
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                      <div className="text-center">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                        <p className="text-sm">Processing your image...</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {previewImage && (
                <div className="flex justify-center items-center w-full mt-3">
                  <Button onClick={onClose} className="pl-4">
                    Shop Now
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right Panel: Product Details */}
          <Card className="h-full">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-4">Selected Item</h2>
              <div className="aspect-[3/4] rounded-lg overflow-hidden mb-4">
                <img
                  src={garmentImage}
                  alt={garmentName}
                  className="object-cover w-full h-full"
                />
              </div>
              <div className="flex items-center justify-between">
                <p className="font-medium text-lg">{garmentName}</p>
                {previewImage && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Check className="h-4 w-4" />
                    Try-on complete
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </WidgetContainer>
  );
}
